import { ReactElement, useCallback } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import type { MetadataUpdater } from '@editor/hooks/requests/useUpdateIfcModelMetadata'
import { FilterButton } from '@ui/actions'
import { useIsUpdatingIfcModelMetadata } from '@editorHooks'

interface Props {
  /** Elements added to the model's filtered elements on click. Button is disabled when empty. */
  guids: string[]
  disabled?: boolean
  updateIfcModelMetadata: (updater: MetadataUpdater) => void
  /** Called after the elements are filtered (e.g. to clear the selection or close the form). */
  onFiltered?: () => void
  /** Register Space as a shortcut for filtering (full-page filter mode). */
  enableSpaceHotkey?: boolean
  dataCy?: string
}

/**
 * Adds `guids` to the model's filtered elements on click (no confirmation). Shared by the IFC
 * Import filter panel and the IFC element form's "Filtern" tab.
 */
const IFCFilterButton = ({
  guids,
  disabled,
  updateIfcModelMetadata,
  onFiltered,
  enableSpaceHotkey,
  dataCy,
}: Props): ReactElement => {
  const isUpdating = useIsUpdatingIfcModelMetadata()

  const handleFilter = useCallback(() => {
    if (guids.length === 0) return
    updateIfcModelMetadata(current => {
      if (!current) return current
      return {
        ...current,
        filteredElements: [...(current.filteredElements ?? []), guids],
      }
    })
    onFiltered?.()
  }, [guids, updateIfcModelMetadata, onFiltered])

  // Space triggers the filter (same as clicking the button). Ignored while typing in a field.
  useHotkeys('space', handleFilter, { enabled: !!enableSpaceHotkey, preventDefault: true }, [
    handleFilter,
  ])

  return (
    <FilterButton
      variant="contained"
      size="small"
      onClick={handleFilter}
      disabled={disabled || guids.length === 0 || isUpdating}
      data-cy={dataCy}
      fullWidth
    >
      {enableSpaceHotkey && guids.length > 0 ? 'Filtern (Leertaste)' : 'Filtern'}
    </FilterButton>
  )
}

export default IFCFilterButton
